/**
 * The Threat Feed's rows, built from rings already read: which incidents form a campaign (item 3),
 * what state each is in, who it touches and how far it reaches. Pure: no request, no clock, so
 * any data source can feed it and a test can state what it should produce. Moved out of
 * `components/ThreatFeed.tsx`; the rules are the ones the feed already had, plus the campaign rule.
 */
import { funnelWindowMs, isEscalatedPlan, linkTargetId, sourceRefId, type PeriodWindow } from '../../lib/funnel';
import type { FeedStateGroup, FeedType } from '../../lib/stageDetail';
import { campaignLabel, incidentLabel } from '../../lib/ids';
import { sourceLabel } from '../../lib/sourceNames';
import { candidateDisplayStatus } from '../../api-utils/threats';
import { blastElementId } from '../../api-utils/blast';
import { ebrScoreOf } from '../../components/c3/dossierModel';
import { toScoreNumber } from '../../components/c3/eriUtils';
import { groupCampaigns } from './campaigns';
import type {
  BlastRadiusScoreDocument,
  BusinessElementDocument,
  InfrastructureElementDocument,
  RemediationItem,
  ThreatElementDocument,
  ThreatEventCandidateItem,
  ThreatEventItem,
} from '../domain/types';

export type Severity = 'critical' | 'high' | 'medium' | 'low';
export type EntityKind = 'business' | 'infra' | 'external';
/**
 * The seven states of the client's definition 4 (notes 10/26), in the order a threat goes through them. The records carry
 * no state of their own: it is read from the steps of the remediation plan (docs/c3/BACKEND.md, "Threat Feed states").
 */
export type FeedStateKind =
  | 'unassigned'
  | 'assigned'
  | 'escalated'
  | 'pending_approval'
  | 'pending_execution'
  | 'closed'
  | 'failed';

export interface FeedThreat {
  event: ThreatEventItem;
  candidate: ThreatEventCandidateItem | null;
  action: ThreatElementDocument | null;
  plan: RemediationItem | null;
  unconsolidated: boolean;
}

export interface EntityChip {
  id: string;
  name: string;
  kind: EntityKind;
}

export interface FeedState {
  kind: FeedStateKind;
  label: string;
  tag?: string;
  count?: number;
}

export interface FeedReach {
  score: number;
  reach: number;
}

/** Per-threat column data for expanded campaign/incident rows. */
export interface ThreatRowView {
  threat: FeedThreat;
  title: string;
  description: string;
  sources: string[];
  severity: Severity;
  firstEventMs: number | null;
  /** The latest time an event or a step of its remediation touched it. */
  lastUpdatedMs: number | null;
  entities: EntityChip[];
  state: FeedState;
  reach: FeedReach | null;
}

export interface FeedRow {
  id: string;
  kind: 'campaign' | 'standalone';
  title: string;
  description: string;
  sources: string[];
  severity: Severity;
  threats: FeedThreat[];
  members: ThreatRowView[];
  campaign?: ThreatElementDocument;
  firstEventMs: number | null;
  lastUpdatedMs: number | null;
  entities: EntityChip[];
  state: FeedState;
  reach: FeedReach | null;
}

export const SEVERITY_RANK: Record<Severity, number> = { low: 1, medium: 2, high: 3, critical: 4 };
/** Active to closed, as the client asked the states to be ordered (notes 11/2). */
export const FEED_STATE_ORDER: readonly FeedStateKind[] = [
  'unassigned',
  'assigned',
  'escalated',
  'pending_approval',
  'pending_execution',
  'closed',
  'failed',
];
const STATE_LABEL: Record<FeedStateKind, string> = {
  unassigned: 'Unassigned',
  assigned: 'Assigned',
  escalated: 'Escalated',
  pending_approval: 'Pending approval',
  pending_execution: 'Pending execution',
  closed: 'Closed',
  failed: 'Failed',
};
const stateOfKind = (kind: FeedStateKind, extra: Partial<FeedState> = {}): FeedState => ({
  kind,
  label: STATE_LABEL[kind],
  ...extra,
});

export function normalize(value: unknown): string {
  return String(value ?? '').trim();
}

export function toSeverity(value: unknown): Severity {
  const severity = normalize(value).toLowerCase();
  return severity === 'critical' || severity === 'high' || severity === 'medium' || severity === 'low'
    ? severity
    : 'low';
}

export function highestSeverity(events: ThreatEventItem[]): Severity {
  return events.reduce<Severity>((highest, event) => {
    const current = toSeverity(event.detection_risk_level);
    return SEVERITY_RANK[current] > SEVERITY_RANK[highest] ? current : highest;
  }, 'low');
}

export function threatTitle(threat: FeedThreat): string {
  return (
    normalize(threat.event.detection_classification) ||
    normalize(threat.action?.name) ||
    'Threat action'
  );
}

export function threatDescription(threat: FeedThreat): string {
  return normalize(threat.event.detection_description) || normalize(threat.action?.description);
}

export function eventStartMs(event: ThreatEventItem): number | null {
  const ms = Date.parse(event.first_event_time || event.created_at || '');
  return Number.isFinite(ms) ? ms : null;
}

/** The latest of its event times and the steps of its remediation (formed, assigned, decided, verified). */
export function threatUpdatedMs(threat: FeedThreat): number | null {
  const stamps = [
    threat.event.last_event_time,
    threat.event.first_event_time,
    threat.event.created_at,
    threat.plan?.formed_at,
    threat.plan?.assigned_at,
    threat.plan?.decided_at,
    threat.plan?.verified_at,
  ]
    .map((value) => Date.parse(value || ''))
    .filter((ms) => Number.isFinite(ms));
  return stamps.length ? Math.max(...stamps) : null;
}

export function remMode(plan: RemediationItem | null): 'auto' | 'gated' | '' {
  const mode = normalize(plan?.execution_mode).toLowerCase();
  if (mode === 'auto') return 'auto';
  if (mode === 'gated') return 'gated';
  return '';
}

/** Whether the analysis has drawn up what to do: the plan lists its operations. */
function analysisDone(plan: RemediationItem): boolean {
  return Array.isArray(plan.operations) && plan.operations.length > 0;
}

/**
 * Where a threat is, from the steps its remediation plan has taken (client notes 10/26, definition 4). The furthest step wins:
 * Failed (cancelled, or reverted after it ran) and Closed (remediated) end it; Pending execution is a plan that runs now or
 * only waits to run (approved, or automatic); Escalated and Pending approval wait on a person. A plan that lists no operations
 * yet is still being drawn up by the analysis: Assigned when someone has it, Unassigned, in the queue, when nobody does.
 * Gated is the default (definition 3), so a plan with no mode waits for approval as a gated one does.
 */
export function deriveThreatState(threat: FeedThreat): FeedState {
  const plan = threat.plan;
  const planStatus = normalize(plan?.status).toLowerCase();
  const detection = normalize(threat.event.detection_status).toLowerCase() || 'active';

  if (planStatus === 'cancelled') return stateOfKind('failed', { tag: 'Rejected' });
  if (planStatus === 'reverted' || detection === 'reverted') return stateOfKind('failed', { tag: 'Reverted' });
  if (detection === 'remediated' || planStatus === 'completed') {
    const mode = remMode(plan);
    return stateOfKind('closed', { tag: mode === 'auto' ? 'Auto' : mode === 'gated' ? 'Manual' : undefined });
  }
  if (detection === 'in_progress' || planStatus === 'in_progress') {
    return stateOfKind('pending_execution', { tag: 'Running' });
  }
  if (!plan) return stateOfKind('unassigned');
  if (isEscalatedPlan(plan)) return stateOfKind('escalated');
  if (!analysisDone(plan)) return stateOfKind(normalize(plan.assigned_at) ? 'assigned' : 'unassigned');
  if (remMode(plan) === 'auto') return stateOfKind('pending_execution', { tag: 'Auto' });
  if (normalize(plan.decided_at)) return stateOfKind('pending_execution', { tag: 'Approved' });
  return stateOfKind('pending_approval', { count: 1 });
}

/**
 * A campaign is as far as its least advanced incident: it is Closed only when every incident is, and Failed when the ones
 * that ended did not all succeed. What waits on a person's approval comes first, with how many incidents wait.
 */
export function deriveCampaignState(threats: FeedThreat[]): FeedState {
  if (threats.length === 0) return stateOfKind('unassigned');
  const states = threats.map(deriveThreatState);
  const approving = states.filter((s) => s.kind === 'pending_approval').length;
  if (approving > 0) return stateOfKind('pending_approval', { count: approving });
  const open = FEED_STATE_ORDER.slice(0, 5).find((kind) => states.some((s) => s.kind === kind));
  if (open) return states.find((s) => s.kind === open) as FeedState;
  return states.find((s) => s.kind === 'failed') ?? states[0];
}

export function collectEntityChips(
  actions: Array<ThreatElementDocument | null | undefined>,
  infraById: Map<string, InfrastructureElementDocument>,
  businessById: Map<string, BusinessElementDocument>
): EntityChip[] {
  const business: EntityChip[] = [];
  const infra: EntityChip[] = [];
  const external: EntityChip[] = [];
  const seen = new Set<string>();

  const push = (bucket: EntityChip[], chip: EntityChip) => {
    if (!chip.id || seen.has(chip.id)) return;
    seen.add(chip.id);
    bucket.push(chip);
  };

  for (const action of actions) {
    if (!action) continue;
    const bizRefs = Array.isArray(action.business_refs) ? action.business_refs : [];
    for (const ref of bizRefs) {
      const id = linkTargetId(ref);
      if (!id || id.startsWith('_dangling/')) {
        if (id) {
          push(external, {
            id,
            name: id.replace(/^_dangling\//, '').slice(-28) || id,
            kind: 'external',
          });
        }
        continue;
      }
      const doc = businessById.get(id);
      push(business, {
        id,
        name: normalize(doc?.name) || id.slice(-8),
        kind: 'business',
      });
    }
    const infraRefs = Array.isArray(action.infrastructure_refs) ? action.infrastructure_refs : [];
    for (const ref of infraRefs) {
      const id = linkTargetId(ref);
      if (!id) continue;
      if (id.startsWith('_dangling/')) {
        push(external, {
          id,
          name: id.replace(/^_dangling\//, '').slice(-28) || id,
          kind: 'external',
        });
        continue;
      }
      const doc = infraById.get(id);
      if (doc) {
        push(infra, {
          id,
          name: normalize(doc.name) || normalize(doc.external_id) || id.slice(-8),
          kind: 'infra',
        });
      } else {
        push(external, {
          id,
          name: id.includes(':') ? id.split('/').pop() || id.slice(-28) : id.slice(-8),
          kind: 'external',
        });
      }
    }
  }

  return [...business, ...infra, ...external];
}

export function reachabilityOf(score: BlastRadiusScoreDocument): number {
  return (
    toScoreNumber(score.reachability_count) ??
    toScoreNumber(score.reachable_nodes?.count) ??
    toScoreNumber(score.reachable_infrastructure_elements?.count) ??
    0
  );
}

export function deriveReach(
  actions: Array<ThreatElementDocument | null | undefined>,
  scoreByElementId: Map<string, BlastRadiusScoreDocument>
): FeedReach | null {
  let best: FeedReach | null = null;
  for (const action of actions) {
    if (!action) continue;
    const refs = Array.isArray(action.infrastructure_refs) ? action.infrastructure_refs : [];
    for (const ref of refs) {
      const id = linkTargetId(ref);
      if (!id || id.startsWith('_dangling/')) continue;
      const row = scoreByElementId.get(id);
      const score = ebrScoreOf(row);
      if (score == null || !row) continue;
      const reach = reachabilityOf(row);
      if (
        !best ||
        score > best.score ||
        (score === best.score && reach > best.reach)
      ) {
        best = { score, reach };
      }
    }
  }
  return best;
}

export function threatSources(threat: FeedThreat): string[] {
  const raw = normalize(threat.candidate?.source || threat.event.provider);
  return raw ? [raw] : [];
}

/**
 * How an incident is remediated (client notes 10/26, definition 3): Gated needs a person to act, Automated does not.
 * Gated is the default, so an incident with no plan yet reads as gated.
 */
export type FeedActionType = 'gated' | 'automated';
export const FEED_ACTION_TYPES: readonly FeedActionType[] = ['gated', 'automated'];
export const FEED_ACTION_LABEL: Record<FeedActionType, string> = { gated: 'Gated', automated: 'Automated' };

export function threatActionType(threat: FeedThreat): FeedActionType {
  return remMode(threat.plan) === 'auto' ? 'automated' : 'gated';
}

/** What the feed's filters have ticked. An empty list is no filter; `period` is null for any time. */
export interface FeedFilters {
  sources: readonly string[];
  severities: readonly Severity[];
  states: readonly FeedStateGroup[];
  types: readonly FeedType[];
  actions: readonly FeedActionType[];
  period: PeriodWindow | null;
}

export const NO_FEED_FILTERS: FeedFilters = {
  sources: [],
  severities: [],
  states: [],
  types: [],
  actions: [],
  period: null,
};

/**
 * Whether a row passes the filters (client notes 10/26, item 14). Within one filter any ticked option is enough; across
 * filters all must hold. A campaign passes a state, an action type or a period when one of its incidents does, because it
 * is those incidents that are in that state, remediated that way or received then.
 */
export function rowMatchesFilters(row: FeedRow, filters: FeedFilters, nowMs: number): boolean {
  const { sources, severities, states, types, actions, period } = filters;
  if (sources.length > 0 && !sources.some((source) => row.sources.includes(source))) return false;
  if (severities.length > 0 && !severities.includes(row.severity)) return false;
  if (types.length > 0 && !types.includes(row.kind === 'campaign' ? 'campaign' : 'incident')) return false;
  if (states.length > 0 && !row.members.some((member) => states.includes(member.state.kind))) return false;
  if (actions.length > 0 && !row.members.some((member) => actions.includes(threatActionType(member.threat)))) return false;
  if (period) {
    const span = funnelWindowMs(period);
    const received = (ms: number | null) => ms != null && nowMs - ms >= 0 && (span == null || nowMs - ms <= span);
    if (!row.members.some((member) => received(member.firstEventMs))) return false;
  }
  return true;
}

/** One incident as a search reads it: what a person could type to find it. */
function threatText(view: ThreatRowView): string[] {
  return [
    view.title,
    view.description,
    incidentLabel(view.threat.event._id),
    view.severity,
    view.state.label,
    ...view.sources.map(sourceLabel),
    ...view.entities.map((entity) => entity.name),
  ];
}

/**
 * Whether a row answers a search (client notes 10/26, items 2 and 3: search belongs to the Threat Feed and finds only
 * what the feed lists). Every word typed has to appear somewhere in the row or in one of its incidents, in any
 * order and in any case, so a campaign is found by what is inside it. An empty search matches everything.
 */
export function rowMatchesQuery(row: FeedRow, query: string): boolean {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const text = [
    row.title,
    row.description,
    row.kind === 'campaign' ? campaignLabel(row.campaign?._id || row.id) : incidentLabel(row.id),
    row.severity,
    row.state.label,
    ...row.sources.map(sourceLabel),
    ...row.entities.map((entity) => entity.name),
    ...row.members.flatMap(threatText),
  ]
    .join(' ')
    .toLowerCase();
  return terms.every((term) => text.includes(term));
}

/** The rings the feed is built from. */
export interface FeedInputs {
  threatEvents: ThreatEventItem[];
  candidates: ThreatEventCandidateItem[];
  elements: ThreatElementDocument[];
  remediations: RemediationItem[];
  infrastructure: InfrastructureElementDocument[];
  business: BusinessElementDocument[];
  blastScores: BlastRadiusScoreDocument[];
}

export interface FeedModel {
  /** Campaigns of two or more incidents, then the incidents that belong to none. Not sorted. */
  rows: FeedRow[];
  /** Alerts the analysis judged false positives, for the Suppressed view. */
  suppressed: ThreatEventCandidateItem[];
}

export function buildFeedModel(inputs: FeedInputs): FeedModel {
  const { threatEvents, candidates, elements, remediations, infrastructure, business, blastScores } = inputs;

  const eventById = new Map(threatEvents.map((event) => [event._id, event]));
  const candidateById = new Map(candidates.map((candidate) => [candidate._id, candidate]));
  const planByThreat = new Map<string, RemediationItem>();
  for (const rem of remediations) {
    if (rem.threat_event_id && !planByThreat.has(rem.threat_event_id)) {
      planByThreat.set(rem.threat_event_id, rem);
    }
  }
  const infraById = new Map(infrastructure.filter((el) => el._id).map((el) => [String(el._id), el]));
  const businessById = new Map(business.filter((el) => el._id).map((el) => [String(el._id), el]));
  const scoreByElementId = new Map<string, BlastRadiusScoreDocument>();
  for (const row of blastScores) {
    const id = blastElementId(row);
    if (id) scoreByElementId.set(id, row);
  }

  const actions = elements.filter((el) => String(el.element_type || '').toLowerCase() === 'action');
  const actionByThreatId = new Map<string, ThreatElementDocument>();
  for (const action of actions) {
    const tid = sourceRefId(action.threat_event_id);
    if (tid) actionByThreatId.set(tid, action);
  }
  const groups = groupCampaigns(elements, threatEvents);
  const memberActionIds = new Set<string>();
  for (const group of groups) group.actions.forEach((action) => memberActionIds.add(action._id || ''));

  const toThreat = (event: ThreatEventItem): FeedThreat => {
    const action = actionByThreatId.get(event._id) || null;
    return {
      event,
      candidate: candidateById.get(event.candidate_id) ?? null,
      action,
      plan: planByThreat.get(event._id) || null,
      unconsolidated: !action,
    };
  };

  const toThreatView = (threat: FeedThreat): ThreatRowView => ({
    threat,
    title: threatTitle(threat),
    description: threatDescription(threat),
    sources: threatSources(threat),
    severity: toSeverity(threat.event.detection_risk_level),
    firstEventMs: eventStartMs(threat.event),
    lastUpdatedMs: threatUpdatedMs(threat),
    entities: collectEntityChips([threat.action], infraById, businessById),
    state: deriveThreatState(threat),
    reach: deriveReach([threat.action], scoreByElementId),
  });

  const campaignRows: FeedRow[] = groups.map(({ campaign, actions: memberActions }, index) => {
    const threats = memberActions
      .map((action) => eventById.get(sourceRefId(action.threat_event_id)))
      .filter((event): event is ThreatEventItem => Boolean(event))
      .map(toThreat);
    const members = threats.map(toThreatView);
    const startTimes = members.map((m) => m.firstEventMs).filter((ms): ms is number => ms != null);
    const updateTimes = members.map((m) => m.lastUpdatedMs).filter((ms): ms is number => ms != null);
    const sources = Array.from(new Set(members.flatMap((m) => m.sources).filter(Boolean))).sort();
    return {
      id: campaign._id || normalize(campaign.name) || `campaign-${index}`,
      kind: 'campaign' as const,
      title: normalize(campaign.name) || 'Untitled campaign',
      description: normalize(campaign.description),
      sources,
      severity: highestSeverity(threats.map((t) => t.event)),
      threats,
      members,
      campaign,
      firstEventMs: startTimes.length ? Math.min(...startTimes) : null,
      lastUpdatedMs: updateTimes.length ? Math.max(...updateTimes) : null,
      entities: collectEntityChips(memberActions, infraById, businessById),
      state: deriveCampaignState(threats),
      reach: deriveReach(memberActions, scoreByElementId),
    };
  });

  const standaloneRows: FeedRow[] = threatEvents
    .filter((event) => {
      const action = actionByThreatId.get(event._id);
      return !action || !memberActionIds.has(action._id || '');
    })
    .map(toThreat)
    .map((threat) => {
      const view = toThreatView(threat);
      return {
        id: threat.event._id,
        kind: 'standalone' as const,
        title: view.title,
        description: view.description,
        sources: view.sources,
        severity: view.severity,
        threats: [threat],
        members: [view],
        firstEventMs: view.firstEventMs,
        lastUpdatedMs: view.lastUpdatedMs,
        entities: view.entities,
        state: view.state,
        reach: view.reach,
      };
    });

  return {
    rows: [...campaignRows, ...standaloneRows],
    suppressed: candidates.filter((candidate) => candidateDisplayStatus(candidate) === 'false_positive'),
  };
}
